import React, { forwardRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import Field from '@starbucks-web/pattern-library/lib/components/field';
import Heading from '@starbucks-web/pattern-library/lib/components/heading';
import Expander from '@starbucks-web/pattern-library/lib/components/expander';
import IconButton from '@starbucks-web/pattern-library/lib/components/icon-button';
import shouldDisplayError from '@starbucks-web/pattern-library/lib/components/form-container/should-display-error';
import trashPath from '@starbucks-web/pattern-library/lib/icons/trash';

import { setSessionGiftFormData } from '../../utils/session-storage-gift';
import { updateNumberOfRecipients } from '../../state/action-creators';
import { getUpdatedRecipientFields } from '../../utils/recipients';

import messages from './messages';
import { formMessages } from 'shared/app/messages/user-form-input.js';
import sharedMessages from 'shared/app/components/text-area/messages.js';

const Recipient = forwardRef(
  (
    {
      currentRecipient,
      currentIndex,
      fields,
      moveFocusOnClick,
      recipientEmail,
      recipientName,
      recipients,
      sendMethod,
      setRecipients,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const emailError = recipientEmail?.errorDetails?.existence
      ? formatMessage(messages.recipientEmailMissing)
      : formatMessage(messages.emailError);

    const recipientNumber = currentIndex + 1;

    const updateRecipientFieldState = ({ e, fieldObj = {} }) => {
      fieldObj?.input?.onBlur(e);
    };

    const updateRecipientLocalState = ({ e, fieldKeyType = '' }) => {
      const newRecips = recipients.map((recipient) => {
        if (
          recipient.id === currentRecipient.id &&
          currentRecipient[fieldKeyType] !== e.target.value
        ) {
          return { ...recipient, [`${fieldKeyType}`]: e.target.value };
        }
        return recipient;
      });
      return setRecipients(newRecips);
    };

    return (
      <React.Fragment>
        {recipients.length > 1 ? (
          <div className="flex justify-between items-center">
            <Heading
              autoFocus
              className="text-upper text-bold"
              ref={ref}
              size="xxs"
              tagName="h3"
            >
              <FormattedMessage
                {...messages.multiRecipientHeader}
                values={{ number: recipientNumber }}
              />
            </Heading>
            <IconButton
              ariaLabel={formatMessage(messages.deleteRecipientLabel, {
                number: recipientNumber,
              })}
              onClick={() => {
                const newRecipients = recipients.filter((recipient) => {
                  return (
                    parseInt(recipient.id) !== parseInt(currentRecipient.id)
                  );
                });

                setRecipients(newRecipients);
                moveFocusOnClick(currentIndex - 1); // this is not quite right but can be improved at a later time
                dispatch(updateNumberOfRecipients(newRecipients?.length));

                const newRecipFields = getUpdatedRecipientFields({
                  recipients: newRecipients,
                  includeFieldShape: false,
                });

                const {
                  amount,
                  customAmount,
                  message,
                  senderEmail,
                  senderName,
                } = fields;
                const newFields = {
                  amount,
                  customAmount,
                  message,
                  senderEmail,
                  senderName,
                  ...newRecipFields,
                };
                setSessionGiftFormData(newFields);
              }}
              path={trashPath}
            />
          </div>
        ) : null}
        <fieldset>
          <legend className="hiddenVisually">
            <FormattedMessage
              {...messages.recipientInformationLabel}
              values={{
                recipientNumber,
                numberOfRecipients: recipients.length,
              }}
            />
          </legend>
          <Field
            className="mb2"
            {...recipientName?.input}
            data-e2e="recipientName"
            error={shouldDisplayError(recipientName)}
            errorMessage={formatMessage(messages.recipientNameError)}
            id={`recipientName${currentRecipient.id}`}
            maxLength={40}
            maxLengthAriaMessage={formatMessage(
              formMessages.maxLengthAriaMessage
            )}
            onBlur={(e) => {
              updateRecipientFieldState({
                e,
                fieldObj: recipientName,
                fieldKey: 'recipientName',
              });
              updateRecipientLocalState({ e, fieldKeyType: 'recipientName' });
            }}
          >
            <FormattedMessage {...messages.recipientNameLabel} />
            <span className="hiddenVisually">
              ,{' '}
              <FormattedMessage
                {...sharedMessages.characterLimitLabel}
                values={{ maxCharacterCount: 40 }}
              />
            </span>
          </Field>
          <Expander isExpanded={sendMethod === 'email'}>
            <Field
              {...recipientEmail?.input}
              data-e2e="recipientEmail"
              error={shouldDisplayError(recipientEmail)}
              errorMessage={emailError}
              id={`recipientEmail${currentRecipient.id}`}
              maxLength={254}
              maxLengthAriaMessage={formatMessage(
                formMessages.maxLengthAriaMessage
              )}
              onBlur={(e) => {
                updateRecipientFieldState({
                  e,
                  fieldObj: recipientEmail,
                  fieldKey: 'recipientEmail',
                });
                updateRecipientLocalState({
                  e,
                  fieldKeyType: 'recipientEmail',
                });
              }}
              type="email"
            >
              <FormattedMessage {...messages.recipientEmailLabel} />
              <span className="hiddenVisually">
                ,{' '}
                <FormattedMessage
                  {...sharedMessages.characterLimitLabel}
                  values={{ maxCharacterCount: 254 }}
                />
              </span>
            </Field>
          </Expander>
        </fieldset>
      </React.Fragment>
    );
  }
);

export default Recipient;
